import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Stack, Alert,
  CircularProgress, Typography, Divider, Chip, Box,
} from '@mui/material';

import {
  useConfirmarTransaccion,
  useRechazarTransaccion,
  useCuponesPendientesDeEmpresa,
} from '../../hooks/useTransacciones';
import { useEmpresasActivas } from '../../hooks/useUsuarios';
import {
  type TransaccionRecaudacion,
  getEstadoTrxLabel,
  getEstadoTrxColor,
} from '../../types/transaccion';
import { formatCurrency, formatDateTime, formatCuit } from '../../utils/format';

type Modo = 'ver' | 'conciliar' | 'rechazar';

interface Props {
  open: boolean;
  trx: TransaccionRecaudacion;
  modo: Modo;
  onClose: () => void;
}

export default function ConciliarDialog({ open, trx, modo, onClose }: Props) {
  const confirmar = useConfirmarTransaccion();
  const rechazar  = useRechazarTransaccion();
  const { data: empresas = [] } = useEmpresasActivas();

  // Si la transacción ya tiene empresa, esa manda. Si está sin_identificar,
  // el operador la elige acá.
  const [empresaIdLocal, setEmpresaIdLocal] = useState<string>(
    trx.empresa_id ?? '',
  );
  const empresaIdEfectiva = trx.empresa_id ?? empresaIdLocal;

  const { data: cuponesDisponibles = [] } = useCuponesPendientesDeEmpresa(
    empresaIdEfectiva || undefined,
  );

  const [cuponId, setCuponId] = useState('');
  const [motivo, setMotivo] = useState('');
  const [motivoError, setMotivoError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setEmpresaIdLocal(trx.empresa_id ?? '');
      setCuponId('');
      setMotivo('');
      setMotivoError(null);
    }
  }, [open, trx.id, trx.empresa_id]);

  const cuponElegido = useMemo(
    () => cuponesDisponibles.find((c) => c.id === cuponId),
    [cuponesDisponibles, cuponId],
  );

  const esParcial    = cuponElegido && trx.monto < cuponElegido.monto_total;
  const esExcedente  = cuponElegido && trx.monto > cuponElegido.monto_total;

  // Posible duplicado detectado por el trigger SQL al insertar la transacción
  // (decisión #028): si hay un pago manual CVU del mismo monto en la ventana
  // configurada, queda anotado en raw_data.posible_duplicado_pago_id.
  const posibleDuplicadoPagoId =
    (trx.raw_data as { posible_duplicado_pago_id?: string } | null)
      ?.posible_duplicado_pago_id ?? null;

  const handleConfirmar = async () => {
    if (!cuponId || !empresaIdEfectiva) return;
    try {
      await confirmar.mutateAsync({
        id:         trx.id,
        cupon_id:   cuponId,
        empresa_id: trx.empresa_id ? undefined : empresaIdEfectiva,
      });
      onClose();
    } catch {
      // se muestra abajo
    }
  };

  const handleRechazar = async () => {
    const m = motivo.trim();
    if (m.length < 5) {
      setMotivoError('Indicá un motivo (mínimo 5 caracteres).');
      return;
    }
    if (m.length > 500) {
      setMotivoError('El motivo no puede superar 500 caracteres.');
      return;
    }
    try {
      await rechazar.mutateAsync({ id: trx.id, motivo: m });
      onClose();
    } catch {
      // se muestra abajo
    }
  };

  const apiError = (confirmar.error || rechazar.error) as Error | null;

  const title =
    modo === 'conciliar' ? 'Conciliar transacción contra un cupón' :
    modo === 'rechazar'  ? 'Rechazar transacción' :
                           'Detalle de la transacción';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {apiError && <Alert severity="error">{apiError.message}</Alert>}

          {/* Warning: posible duplicado de pago manual CVU (decisión #028) */}
          {posibleDuplicadoPagoId && modo !== 'ver' && (
            <Alert severity="warning">
              <Typography variant="body2" fontWeight={600}>
                Posible duplicado detectado
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Esta transacción coincide en monto y empresa con un{' '}
                <strong>pago CVU registrado manualmente</strong> en los últimos
                días. Si se trata del mismo evento, <strong>rechazá</strong>{' '}
                esta transacción con motivo "duplicado de pago manual" para no
                duplicar el cobro. Pago candidato:{' '}
                <code style={{ fontSize: 11 }}>
                  {posibleDuplicadoPagoId.slice(0, 8)}…
                </code>
              </Typography>
            </Alert>
          )}

          {/* Resumen de la transacción */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="caption" color="text.secondary">Referencia externa</Typography>
              <Typography variant="body2" fontFamily="monospace">
                {trx.referencia_externa ?? '—'}
              </Typography>
            </Box>
            <Chip
              label={getEstadoTrxLabel(trx.estado)}
              color={getEstadoTrxColor(trx.estado)}
              size="small"
            />
          </Stack>

          <Divider />

          <Stack spacing={1}>
            <Row label="Monto" value={formatCurrency(trx.monto)} bold />
            <Row label="Fecha de acreditación" value={formatDateTime(trx.fecha_acreditacion)} />
            <Row label="Canal" value={trx.canal} />
            <Row label="Proveedor" value={trx.proveedor} />
            {trx.cvu_empresa && (
              <>
                <Row label="CVU destino" value={trx.cvu_empresa.cvu} />
                <Row label="Alias" value={trx.cvu_empresa.alias} />
              </>
            )}
            {trx.empresa && (
              <Row
                label="Empresa identificada"
                value={`${trx.empresa.razon_social} (${formatCuit(trx.empresa.cuit)})`}
              />
            )}
          </Stack>

          {/* Selector de empresa — solo si sin_identificar y modo conciliar */}
          {modo === 'conciliar' && !trx.empresa_id && (
            <>
              <Divider />
              <Alert severity="info" sx={{ fontSize: 13 }}>
                Esta transacción llegó <strong>sin identificar</strong>. Asigná la
                empresa antes de elegir el cupón.
              </Alert>
              <TextField
                select
                label="Empresa"
                required
                fullWidth
                value={empresaIdLocal}
                onChange={(e) => setEmpresaIdLocal(e.target.value)}
                helperText={`${empresas.length} empresas activas`}
              >
                <MenuItem value=""><em>Seleccioná empresa…</em></MenuItem>
                {empresas.map((e) => (
                  <MenuItem key={e.id} value={e.id}>
                    {e.razon_social} — {formatCuit(e.cuit)}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}

          {/* Selector de cupón — modo conciliar */}
          {modo === 'conciliar' && empresaIdEfectiva && (
            <>
              <Divider />
              <TextField
                select
                label="Cupón a acreditar"
                required
                fullWidth
                value={cuponId}
                onChange={(e) => setCuponId(e.target.value)}
                disabled={cuponesDisponibles.length === 0}
                helperText={
                  cuponesDisponibles.length === 0
                    ? 'No hay cupones pendientes para esta empresa'
                    : `${cuponesDisponibles.length} cupón(es) pendiente(s)`
                }
              >
                <MenuItem value=""><em>Seleccioná un cupón…</em></MenuItem>
                {cuponesDisponibles.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.codigo} — {formatCurrency(c.monto_total)} ·{' '}
                    {c.periodo?.periodo ?? ''}
                  </MenuItem>
                ))}
              </TextField>

              {cuponElegido && (esParcial || esExcedente) && (
                <Alert severity={esExcedente ? 'warning' : 'info'} sx={{ fontSize: 13 }}>
                  {esParcial && (
                    <>
                      <strong>Pago parcial:</strong> faltarán{' '}
                      {formatCurrency(cuponElegido.monto_total - trx.monto)} para
                      cubrir el cupón. El cupón sigue como pendiente.
                    </>
                  )}
                  {esExcedente && (
                    <>
                      <strong>Excedente:</strong>{' '}
                      {formatCurrency(trx.monto - cuponElegido.monto_total)}{' '}
                      más del total. Se acredita igual; el excedente queda como
                      saldo a favor.
                    </>
                  )}
                </Alert>
              )}
            </>
          )}

          {/* Input de motivo — modo rechazar */}
          {modo === 'rechazar' && (
            <>
              <Divider />
              <TextField
                label="Motivo del rechazo"
                required
                fullWidth
                multiline
                rows={3}
                value={motivo}
                onChange={(e) => {
                  setMotivo(e.target.value);
                  setMotivoError(null);
                }}
                error={!!motivoError}
                helperText={
                  motivoError ??
                  `Mínimo 5 caracteres · ${motivo.trim().length}/500. Se persiste en auditoría.`
                }
                inputProps={{ maxLength: 500 }}
              />
            </>
          )}

          {/* Acreditación previa (modo ver) */}
          {modo === 'ver' && trx.estado === 'acreditada' && trx.fecha_confirmacion && (
            <Alert severity="success">
              Acreditada el {formatDateTime(trx.fecha_confirmacion)}
              {trx.pago_id && ' — pago vinculado generado.'}
            </Alert>
          )}
          {modo === 'ver' && trx.estado === 'rechazada' && (
            <Alert severity="error">
              <Typography variant="body2">
                Rechazada
                {trx.fecha_confirmacion && ` el ${formatDateTime(trx.fecha_confirmacion)}`}
              </Typography>
              {(trx.raw_data as { rechazo?: { motivo?: string } })?.rechazo?.motivo && (
                <Typography variant="body2">
                  Motivo:{' '}
                  <em>{(trx.raw_data as { rechazo: { motivo: string } }).rechazo.motivo}</em>
                </Typography>
              )}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={confirmar.isPending || rechazar.isPending}
        >
          {modo === 'ver' ? 'Cerrar' : 'Cancelar'}
        </Button>

        {modo === 'conciliar' && (
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirmar}
            disabled={!cuponId || !empresaIdEfectiva || confirmar.isPending}
            startIcon={
              confirmar.isPending ? <CircularProgress size={18} color="inherit" /> : null
            }
          >
            {confirmar.isPending ? 'Acreditando…' : 'Confirmar acreditación'}
          </Button>
        )}

        {modo === 'rechazar' && (
          <Button
            variant="contained"
            color="error"
            onClick={handleRechazar}
            disabled={rechazar.isPending}
            startIcon={
              rechazar.isPending ? <CircularProgress size={18} color="inherit" /> : null
            }
          >
            {rechazar.isPending ? 'Rechazando…' : 'Rechazar transacción'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={bold ? 600 : 400} sx={{ textAlign: 'right' }}>
        {value}
      </Typography>
    </Stack>
  );
}
